// Shared chrome for the Opportunity Room — the primitives every chapter draws
// with. Kept dumb: no filter state, no data fetching.

import { createContext, useContext } from "react"
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts"
import { Maximize02 } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import { HEADLINE, MINT_PALE } from "./room-model"

/* ── Chart-expand plumbing ────────────────────────────── */
// A chapter card whose children is a render function opts into click-to-
// enlarge. The orchestrator provides `openExpand`; without a provider the
// card renders inline with no affordance (so the split stays behavior-safe).

export type ExpandPayload = { title: string; sub?: string; render: () => React.ReactNode }
export const ExpandContext = createContext<((p: ExpandPayload) => void) | null>(null)

type CardChildren = React.ReactNode | ((ctx: { expanded: boolean }) => React.ReactNode)

export function ChartCard({ title, sub, className, children, info }: {
  title: string; sub?: string; className?: string; children: CardChildren; info?: string
}) {
  const openExpand = useContext(ExpandContext)
  const isFn = typeof children === "function"
  const expandable = isFn && !!openExpand
  const body = isFn ? (children as (c: { expanded: boolean }) => React.ReactNode)({ expanded: false }) : children

  const activate = () => {
    if (!expandable) return
    openExpand!({ title, sub, render: () => (children as (c: { expanded: boolean }) => React.ReactNode)({ expanded: true }) })
  }

  return (
    <div
      className={cx(
        "group/card relative min-w-0 rounded-2xl border border-[#ECEBE6] bg-white p-6 transition-all duration-150 print:break-inside-avoid print:shadow-none",
        expandable ? "cursor-zoom-in hover:border-[#D6D3C8]/80 hover:shadow-sm" : "hover:border-[#D6D3C8]/80 hover:shadow-sm",
        className,
      )}
      {...(expandable
        ? {
            role: "button" as const, tabIndex: 0, "aria-label": `Enlarge ${title}`,
            onClick: (e: React.MouseEvent) => {
              if (!(e.target as HTMLElement).closest("button,a,input,[role=tab],[data-no-expand]")) activate()
            },
            onKeyDown: (e: React.KeyboardEvent) => {
              if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) { e.preventDefault(); activate() }
            },
          }
        : {})}
    >
      {expandable && (
        <Maximize02 className="absolute top-4 right-4 size-4 text-[#CFCDC4] opacity-0 group-hover/card:opacity-100 transition-opacity pointer-events-none" aria-hidden />
      )}
      <p className="text-sm font-semibold text-zinc-900">{title}</p>
      {sub !== undefined && (
        <p className="text-xs text-[#8B897E] mt-0.5 mb-4">
          {sub}
          {info && (
            <span className="group/info relative inline-flex items-center justify-center size-3.5 ml-1.5 rounded-full border border-zinc-300 text-[9px] text-[#8B897E] cursor-default align-middle">
              i
              <span className="hidden group-hover/info:block absolute bottom-5 left-1/2 -translate-x-1/2 w-56 rounded-lg bg-zinc-900 px-3 py-2 text-[11px] leading-relaxed text-zinc-100 z-10 normal-case font-normal text-left">
                {info}
              </span>
            </span>
          )}
        </p>
      )}
      {body}
    </div>
  )
}

/* ── Section heading (v4 eyebrow idiom + audience label) ── */

export function SectionHeading({ eyebrow, title, first }: {
  eyebrow: string; title: string; audience?: string; first?: boolean
}) {
  return (
    <div className={cx("mb-4", first ? "mt-0" : "mt-12 pt-8 border-t border-[#E3E1DA]")}>
      <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#0F6E56] mb-1">{eyebrow}</p>
      <h2 className="text-[19px] font-bold text-zinc-900 leading-tight tracking-tight" style={HEADLINE}>{title}</h2>
    </div>
  )
}

/** A deep-linkable chapter: `data-chapter` drives the scrollspy, `id` the anchor. */
export function ChapterSection({ id, eyebrow, title, audience, first, children }: {
  id: string; eyebrow: string; title: string; audience?: string; first?: boolean; children: React.ReactNode
}) {
  return (
    <section
      id={`chapter-${id}`} data-chapter={id}
      className="scroll-mt-[calc(var(--room-rail-h,72px)+16px)]"
    >
      <SectionHeading eyebrow={eyebrow} title={title} audience={audience} first={first} />
      {children}
    </section>
  )
}

/* ── Legend / donut / tooltips ────────────────────────── */

export function LegendRow({ items }: { items: { label: string; color: string; outlined?: boolean }[] }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
      {items.map(it => (
        <span key={it.label} className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#6E6C64]">
          <span className="size-2.5 rounded-[3px] shrink-0" style={it.outlined ? { background: it.color, border: `1px solid ${MINT_PALE}` } : { background: it.color }} />
          {it.label}
        </span>
      ))}
    </div>
  )
}

export function Donut({ data, center, centerSub, height = 170 }: {
  data: { name: string; value: number; color: string }[]
  center: string; centerSub: string; height?: number
}) {
  const filtered = data.filter(d => d.value > 0)
  return (
    <div className="relative" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={filtered} dataKey="value" nameKey="name" innerRadius="68%" outerRadius="95%"
            paddingAngle={2} stroke="#fff" strokeWidth={2}>
            {filtered.map(d => <Cell key={d.name} fill={d.color} />)}
          </Pie>
          <Tooltip content={<DonutTip />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 grid place-items-center pointer-events-none">
        <div className="text-center">
          <p className="text-lg font-bold text-zinc-900 tabular-nums leading-none">{center}</p>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#8B897E] mt-1.5">{centerSub}</p>
        </div>
      </div>
    </div>
  )
}

export function DonutTip({ active, payload }: { active?: boolean; payload?: { name: string; value: number; payload: { tip?: string } }[] }) {
  if (!active || !payload?.length) return null
  const p = payload[0]
  return (
    <div className="rounded-lg bg-zinc-900 px-3 py-2 text-xs text-zinc-100 shadow-lg">
      <span className="font-semibold">{p.name}</span> · {p.payload.tip ?? p.value.toLocaleString()}
    </div>
  )
}

export function BarTip({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; payload: { tip?: string } }[]; label?: string }) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg bg-zinc-900 px-3 py-2 text-xs text-zinc-100 shadow-lg">
      {label && <p className="font-semibold mb-0.5">{label}</p>}
      {payload[0].payload.tip
        ? <p>{payload[0].payload.tip}</p>
        : payload.map(p => <p key={p.name}>{p.name}: <span className="tabular-nums">{p.value.toLocaleString()}</span></p>)}
    </div>
  )
}

/** Tiny in-tile sparkline. */
export function MiniSpark({ values, accent = "#1D9E75" }: { values: number[]; accent?: string }) {
  const max = Math.max(...values, 1)
  return (
    <div className="flex items-end gap-[2px] h-8 mt-2" aria-hidden>
      {values.map((v, i) => (
        <div key={i} className="flex-1 rounded-[1.5px]"
          style={{
            height: `${Math.max((v / max) * 100, 8)}%`,
            background: i === values.length - 1 ? accent : `${accent}66`,
          }} />
      ))}
    </div>
  )
}

/* ── Highlight — the analyst voice, inside the card it proves (the CEO's own
      dist-highlight anatomy: dot + teal-deep + 650). Clickable when the
      finding names a filterable dimension → filter + receipts. ── */

export function Highlight({ text, onClick }: { text: string; onClick?: () => void }) {
  const inner = (
    <>
      <span className="size-2.5 rounded-[3px] shrink-0" style={{ background: "#0D6B51" }} />
      <span className="text-left">{text}</span>
    </>
  )
  return onClick ? (
    <button onClick={onClick}
      className="flex items-center gap-1.5 text-[12px] font-semibold text-[#0F6E56] mb-3 hover:underline underline-offset-2 decoration-[#9FE1CB]">
      {inner}
    </button>
  ) : (
    <p className="flex items-center gap-1.5 text-[12px] font-semibold text-[#0F6E56] mb-3">{inner}</p>
  )
}

/* ── KPI card (the CEO's card anatomy: label → big number → sub → mini-viz
      pinned to the bottom). Every stat card on the screen uses this. ── */

export function KpiCard({ label, value, valueSuffix, sub, viz, className }: {
  label: string
  value: string
  /** Quiet suffix beside the value, e.g. "/ 12". */
  valueSuffix?: string
  sub?: React.ReactNode
  /** Mini-visualization pinned to the card bottom (spark / fill bar / bullet / dots). */
  viz?: React.ReactNode
  className?: string
}) {
  return (
    <div className={cx("animate-element rounded-2xl border border-[#ECEBE6] bg-white p-4 flex flex-col min-w-0 print:break-inside-avoid", className)}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#8B897E] mb-2">{label}</p>
      <p className="text-[25px] font-bold tabular-nums leading-none tracking-tight text-zinc-900">
        {value}{valueSuffix && <span className="text-sm font-normal text-[#8B897E]"> {valueSuffix}</span>}
      </p>
      {sub && <p className="text-[11.5px] text-[#8B897E] mt-1.5 leading-snug">{sub}</p>}
      {viz && <div className="mt-auto pt-3">{viz}</div>}
    </div>
  )
}

/* ── One pill grammar (the CEO's .tab): rounded-full, active = deep-mint fill ── */

export function PillTabs<K extends string>({ items, active, onSelect, size = "md", ariaLabel }: {
  items: { key: K; label: string; count?: number }[]
  active: K | string
  onSelect: (key: K) => void
  size?: "sm" | "md"
  ariaLabel: string
}) {
  return (
    <div className="flex gap-1.5 flex-wrap" role="group" aria-label={ariaLabel}>
      {items.map(t => {
        const isActive = active === t.key
        return (
          <button key={t.key} aria-pressed={isActive} onClick={() => onSelect(t.key)}
            className={cx(
              "rounded-full font-semibold border transition-colors",
              size === "sm" ? "px-3 py-1 text-[12px]" : "px-3.5 py-1.5 text-[12.5px]",
              isActive ? "bg-[#0F6E56] border-[#0F6E56] text-white" : "border-[#E3E1DA] text-[#6E6C64] hover:text-zinc-800 hover:border-[#D6D3C8]",
            )}>
            {t.label}
            {t.count != null && <span className={cx("ml-1.5 tabular-nums", isActive ? "text-[#9FE1CB]" : "text-[#8B897E]")}>{t.count}</span>}
          </button>
        )
      })}
    </div>
  )
}

/** Mini fill bar (his lane-capture pattern): 0→max with a % fill. */
export function FillBar({ pct, maxLabel }: { pct: number; maxLabel?: string }) {
  return (
    <div>
      <div className="h-3 rounded-full bg-[#F1F0EB] overflow-hidden">
        <div className="h-full rounded-full bg-[#1D9E75] transition-all duration-300" style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      {maxLabel && <div className="flex justify-between text-[10px] text-[#8B897E] tabular-nums mt-1"><span>0</span><span>{maxLabel}</span></div>}
    </div>
  )
}

/** Mini bullet vs a target (his bullet pattern): value fill + target tick. */
export function Bullet({ value, target, max, note }: { value: number; target: number; max: number; note?: React.ReactNode }) {
  return (
    <div>
      <div className="relative h-[20px] rounded-md bg-[#F1F0EB]">
        <div className="absolute top-[4px] left-0 h-3 rounded-r-[3px] bg-[#1D9E75] transition-all duration-300" style={{ width: `${Math.min((value / max) * 100, 100)}%` }} />
        <div className="absolute -top-[3px] w-[2.5px] h-[26px] rounded bg-zinc-900" style={{ left: `${Math.min((target / max) * 100, 98)}%` }} />
      </div>
      {note && <p className="text-[11px] mt-1.5">{note}</p>}
    </div>
  )
}

/* ── v4 pills + stat tiles ────────────────────────────── */

const PILL_TONE: Record<string, string> = { high: "#0F6E56", mid: "#BA7517", low: "#71717A" }
export function MakePillRow({ pills }: {
  pills: { name: string; value: string; count?: string; tone?: "high" | "mid" | "low" }[]
}) {
  return (
    <div className="flex gap-2 flex-wrap mt-3">
      {pills.map(p => (
        <div key={p.name} className="flex-1 min-w-[84px] rounded-lg bg-[#F1F0EB] px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#8B897E]">{p.name}</p>
          <p className="text-sm font-bold tabular-nums leading-tight" style={{ color: p.tone ? PILL_TONE[p.tone] : "#18181b" }}>{p.value}</p>
          {p.count && <p className="text-[10px] text-[#8B897E] mt-0.5">{p.count}</p>}
        </div>
      ))}
    </div>
  )
}

export function StatTile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "mint" }) {
  return (
    <div className="rounded-lg bg-[#F1F0EB] px-3 py-2.5 min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#8B897E] mb-1">{label}</p>
      <p className={cx("text-[17px] font-bold tabular-nums leading-none", tone === "mint" ? "text-[#0F6E56]" : "text-zinc-900")}>{value}</p>
      {sub && <p className="text-[10px] text-[#8B897E] mt-1 leading-snug">{sub}</p>}
    </div>
  )
}

/* ── Loading skeleton ─────────────────────────────────── */

export function DashboardSkeleton() {
  return (
    <div className="animate-pulse" aria-busy>
      <div className="h-8 w-72 rounded-lg bg-[#F1F0EB] mb-2" />
      <div className="h-4 w-96 rounded bg-[#F1F0EB] mb-8" />
      <div className="h-12 rounded-2xl bg-[#F1F0EB] mb-5" />
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 xl:col-span-9 space-y-3">
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 md:col-span-5 h-52 rounded-2xl bg-[#F1F0EB]" />
            <div className="col-span-12 md:col-span-7 grid grid-cols-2 gap-3">
              {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 rounded-2xl bg-[#F1F0EB]" />)}
            </div>
          </div>
          {Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-64 rounded-2xl bg-[#F1F0EB]" />)}
        </div>
        <div className="col-span-12 xl:col-span-3 h-96 rounded-2xl bg-[#F1F0EB]" />
      </div>
    </div>
  )
}
